import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import { getUserCountryCodeFromState } from 'selectors/app-selector';
import { deliveryDetailOverlayVisibleSelector } from 'selectors/new-booking-selectors';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import {
  getOfferType,
  isAutoApplyOfferType,
  truncateText,
} from 'helper/anytime-booking';
import promoIcon from 'public/icons/promo-icon.svg';
import warningIcon from 'public/icons/warning-yellow.svg';
import { Icon, Tooltip, Button } from 'grab-ui';
import ScribeDuck from 'ducks/scribe-duck';

import { pushNotificationAction } from 'actions/app-actions';
import styles from './offer-configuration-field.less';
import { NOTIFICATION_TYPE } from '../../notification/notification';
import usePrevious from '../../../../common/usePrevious';

const NAME_MAX_LENGTH = 18;

const OfferConfigurationField = ({ className, delivery, dispatch, index }) => {
  const { detail: deliveryDetail } = delivery;
  const pickUp = _get(deliveryDetail, 'pickUp.poi');
  const dropOff = _get(deliveryDetail, 'dropOffs[0].poi');
  const selectedServiceType = _get(deliveryDetail, 'configuration.serviceType');
  const disabled = !pickUp || !dropOff || !selectedServiceType;
  const offer = _get(deliveryDetail, 'configuration.offerConfig');
  const fetchingQuote = _get(delivery, 'fetchingQuote');
  const isAutoApplyPromo = isAutoApplyOfferType(getOfferType(offer));
  const prevOffer = usePrevious(offer);
  const prevIsAutoPromo = isAutoApplyOfferType(getOfferType(prevOffer));
  const offerID = _get(offer, 'id');

  const detailOverlayHandler = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.fetchOfferList(delivery.id));
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          delivery.id,
          newBookingOverlayType.offerConfiguration,
        ),
      );

      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailApplyOffer(index),
      );
    },
    [dispatch, delivery, index],
  );

  const [hasError, errorMessage] = useMemo(
    () => {
      const paxExpressError = _get(delivery, 'paxExpressError');

      const discountEligibilityError = _get(
        offer,
        'discountEligibilityError',
        null,
      );
      const hasError = !!paxExpressError || !!discountEligibilityError;
      const errorMessage = paxExpressError
        ? _get(paxExpressError, 'message')
        : _get(discountEligibilityError, 'localizedMessage') ||
          I18n.t('v2.error.invalidPromo');
      return [hasError, errorMessage];
    },
    [delivery, offer],
  );

  const offerIconPrefix = useMemo(
    () => {
      if (!offer) {
        return <img className={styles.icon} src={promoIcon} alt="" />;
      }

      if (!hasError) {
        return <img className={styles.icon} src={promoIcon} alt="" />;
      }

      if (fetchingQuote && offer) {
        return <Icon type="loading" style={{ fontSize: 24 }} spin />;
      }

      return (
        <Tooltip title={errorMessage} placement={'top'}>
          <img className={styles.icon} src={warningIcon} alt="" />
        </Tooltip>
      );
    },
    [dispatch, errorMessage, fetchingQuote, hasError, offer],
  );

  useEffect(
    () => {
      const hasApplied = !fetchingQuote && (hasError || offerID);
      if (hasApplied) {
        dispatch(
          ScribeDuck.creators.scribeExpressReviewOfferListApplyOffer(
            !hasError ? 'SUCCESS' : 'FAILED',
          ),
        );
      }
    },
    [dispatch, hasError, offerID, fetchingQuote],
  );

  const renderConfigField = useCallback(
    () => (
      <div
        onClick={disabled ? undefined : detailOverlayHandler}
        className={cx(styles.content, { [styles.disabled]: disabled })}
      >
        {offerIconPrefix}
        <div className={styles.selectedOption}>
          <span>
            {offer
              ? truncateText(_get(offer, 'name'), NAME_MAX_LENGTH)
              : I18n.t(
                  'phrase.offers.mktplace_offers_heading_apply_offers_phase1',
                )}
          </span>
        </div>
      </div>
    ),
    [offerIconPrefix, detailOverlayHandler, disabled, offer],
  );

  useEffect(
    () => {
      if (isAutoApplyPromo) {
        dispatch(
          pushNotificationAction({
            id: 'auto-apply-promo',
            type: NOTIFICATION_TYPE.CONFIRMED,
            message: I18n.t('v2.anytimeBooking.notification.autoPromo.title'),
            description: I18n.t(
              'v2.anytimeBooking.notification.autoPromo.description',
            ),
            link: (
              <Button
                className={styles.button}
                onClick={() => detailOverlayHandler()}
              >
                {I18n.t('v2.anytimeBooking.notification.autoPromo.here')}
              </Button>
            ),
          }),
        );
      }
    },
    [dispatch, isAutoApplyPromo],
  );

  useEffect(
    () => {
      if (prevIsAutoPromo && !offer) {
        // Change delivery options
        dispatch(
          pushNotificationAction({
            id: 'auto-apply-promo',
            type: NOTIFICATION_TYPE.WARNING,
            message: 'Offer has been removed',
            description:
              'Your offer is no longer valid due to changes in Delivery options',
          }),
        );
      }
    },
    [dispatch, offer, prevIsAutoPromo],
  );

  return (
    <div className={cx(styles.promoConfigField, className)}>
      {disabled ? (
        <Tooltip
          title={I18n.t('v2.anytimeBooking.tooltip.offerDisabled')}
          placement={'bottom'}
        >
          {renderConfigField()}
        </Tooltip>
      ) : (
        renderConfigField()
      )}
    </div>
  );
};

OfferConfigurationField.defaultProps = {
  className: undefined,
  detailOverlayVisible: false,
};

OfferConfigurationField.propTypes = {
  className: PropTypes.string,
  delivery: NewBookingDeliveryPropType.isRequired,
  dispatch: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default connect((state, props) => ({
  detailOverlayVisible: deliveryDetailOverlayVisibleSelector(state),
  countryCode: getUserCountryCodeFromState(state),
}))(OfferConfigurationField);
